import { datadogRum } from "@datadog/browser-rum";
import { AppRoute } from "app";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useEffect } from "react";
import { useQuery } from "react-query";

import { NumberString } from "pages/VehicleCondition/types";

import { getSession } from "api/getSession";
import { BFFError, errorMap } from "app/constants";
import { clientConfigAtom } from "atoms/clientConfig";
import { serverSessionAtom } from "atoms/serverSession";
import { sessionIdAtom } from "atoms/sessionId";
import { sessionConfigAtom } from "atoms/sessionConfig"
import { sessionMileageAtom } from "atoms/vehicleCondition/mileage";
import { getUserLocale } from "utils/locale/userLocale";
import { formatNumberIntl } from "utils/stringHelper";

import { clientIdAtom } from "../../atoms/clientId";
import { sessionVin } from "../../atoms/vin/vin";
import { useNavigate } from "react-router-dom";

type UseValidateSessionIdOptions = {
  /**
   * This function will be called once the session has been
   * validated. It will be passed the valid sessionId.
   */
  onSuccess?: (sessionId: string) => void;
};

export const useValidateSessionId = ({ onSuccess }: UseValidateSessionIdOptions = {}) => {
  const [, sessionId] = window.location.pathname.split("/");

  const setServerSession = useSetAtom(serverSessionAtom);
  const setSessionId = useSetAtom(sessionIdAtom);
  const setSessionConfig = useSetAtom(sessionConfigAtom);
  const [mileage, setMileage] = useAtom(sessionMileageAtom(sessionId));
  const [vin, setVin] = useAtom(sessionVin(sessionId));
  const {
    locale,
    vehicleCondition: { showMileage },
    vin: vinConfig
  } = useAtomValue(clientConfigAtom);
  const navigate = useNavigate();
    
  const clientId = useAtomValue(clientIdAtom);
  const { error } = useQuery("getSession", () => getSession(sessionId, clientId), {
    enabled: navigator.onLine && sessionId !== "error",
    suspense: true,
    useErrorBoundary: false,
    retry: false,
    onSuccess: ({ data }) => {
      // setSessionId should happen before anything other atoms update
      setSessionId(sessionId);

      setServerSession(data);
      setSessionConfig(data.sessionConfig)
      if (showMileage && mileage === "" && data.mileage)
        setMileage(formatNumberIntl(data.mileage as NumberString, getUserLocale(locale)));
      if (vinConfig && vin === "" && data.vin) setVin(data.vin);

      if (onSuccess) {
        onSuccess(sessionId);
      }
    },
    onError: (error) => {
      datadogRum.addError(error, {
        message: "Failed to fetch session",
      });
    },
  });

  useEffect(() => {
    if (!error) return;

    const errorResponse = error as any;
    const { errorCode } = errorResponse.response.data;
    const errorPagePath = errorMap[errorCode as BFFError] || AppRoute.InvalidSession;

    navigate(errorPagePath);
  }, [error, navigate]);
};
